import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CallView } from "@gioia365/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../api/http";
import { tabKey, type TabContext, type TabKey } from "../session/tab-context";
import { currentAudioChoice, subscribeAudioChoice } from "./audio-devices";
import type { BrowserPhone, BrowserPhoneError } from "./browser-phone";
import { dial, hangUpCall, type Attempt, type DialContext } from "./call-dial";
import {
  IDLE,
  isCallLive,
  newRequestId,
  numberDiagnosisOf,
  type CallSession,
  type CallSessionApi,
  type CallTarget,
  type OutcomeOrigin,
  type OutcomeDraft,
  type OutcomeSave,
  type OutcomeSaved,
  type StartCallInput,
} from "./call-session-state";
import { sendOutcome } from "./outcome-retry";
import { reportPhoneError } from "./phone-error-report";
import { openCallKey, refreshAfterCall, requestCallAbandon, requestCallView, requestOutcomeSave, requestTelephonyToken, type OutcomeInput } from "./telephony-queries";

export { isCallLive, numberDiagnosisOf } from "./call-session-state";
export type { CallSession, CallSessionApi, CallSessionError, CallTarget, OngoingSession, OutcomeDraft, OutcomeSave, OutcomeSaved, StartCallInput } from "./call-session-state";

// La chiamata in corso della scheda del browser (piano M4, Task 13, D10): vive nel guscio dell'applicazione
// (`CallSessionProvider` in `AppShell.tsx`), quindi cambiare pagina non la interrompe; la postazione della scheda Lead
// (`leads/card/CallDesk.tsx`) e la barra in alto (`CallBar.tsx`) la mostrano, e il Power Dialer (M5), montato dentro
// questo provider, ne osserva le fasi. Una chiamata alla volta per scheda del browser: due schede su due aziende hanno due
// sessioni e due token, legati all'azienda della scheda in cui la chiamata è nata.
//
// Le fasi di `session.phase`, in ordine:
// - `idle`: nessuna chiamata; si può chiamare;
// - `starting`: «Chiama» premuto; il server crea la chiamata (`call` arriva qui), poi il token e il telefono del browser
//   compongono con il solo `callId` (D4; `call-dial.ts`);
// - `ringing`: la chiamata è composta e squilla; `startedAt` è l'inizio del tempo della postazione;
// - `live`: il Lead ha risposto;
// - `outcome`: la chiamata è chiusa (da chi chiama con «Chiudi la chiamata», da chi risponde, o ripresa con «Manca
//   l'esito…», `missing`) e chiede l'esito; `save` dice a che punto è il suo salvataggio. Si esce soltanto con `close()`,
//   che vale quando `stepsDone` è vero: l'esito confermato dal server e nessun passo obbligatorio dopo l'esito ancora da
//   fare (li dice la postazione, `OutcomeSteps.tsx`), oppure, per una chiamata della cascata senza risposta (`unanswered`),
//   con l'esito di Gioia365 preso da `acceptAutomaticOutcome` (TEL-32);
// - `error`: la chiamata non è partita (rifiuto del server, microfono negato, SDK che non si carica, errore del fornitore
//   prima della risposta): la chiamata, se il server l'aveva creata, è già stata abbandonata sul server. Anche una chiamata
//   finita senza arrivare al Lead, di cui il server rifiuta l'esito (`409 invalid_state`), arriva qui (`not_connected`).
//   Si può chiamare di nuovo.
// Il valore del provider cambia soltanto quando cambiano la sessione, `stepsDone`, i passi da fare (`pendingSteps`, che la
// barra usa per dire che cosa manca) o quelli fatti (`doneSteps`, piano M5, Task 17); le funzioni restano le stesse.
//
// Il token del browser si chiede alla prima chiamata e si riusa finché mancano almeno due minuti alla scadenza (D10), uno
// per azienda della scheda. L'esito si salva con una chiave sola per tentativo della postazione, la stessa a ogni nuovo
// invio: un guasto passeggero si riprova da solo finché la pagina è aperta (`outcome-retry.ts`, questione 161), e la
// postazione si chiude soltanto quando il server conferma. Chiudere o ricaricare la pagina durante una chiamata chiede
// conferma (`beforeunload`, soltanto fra `starting` e `live`). Dopo ogni chiamata la scheda del Lead, la Timeline e le
// righe degli elenchi si rileggono (`refreshAfterCall`).
//
// Il telefono del browser si scarica in sottofondo appena il guscio di chi chiama è pronto (`usePreloadBrowserPhone`, chiamato
// da `DialerSessionProvider` con la stessa condizione di chi chiama): la prima chiamata non aspetta il SDK, e una scheda aperta
// prima di un aggiornamento chiama lo stesso.
//
// Il microfono e l'ascolto (richiesta del product owner del 6 ottobre 2026, `audio-devices.ts`): ogni chiamata parte con
// quelli scelti su questo computer, e una scelta nuova fatta nella postazione mentre la chiamata è in corso passa subito
// alla chiamata (`BrowserCall.switchAudio`); a chiamata chiusa vale per la prossima.

/** Il token si riusa finché mancano almeno due minuti alla sua scadenza (D10). */
const TOKEN_MARGIN_MS = 2 * 60_000;

/** Quanto si aspetta, senza `requestIdleCallback`, prima di scaricare il telefono del browser. */
const PRELOAD_FALLBACK_MS = 2_000;

const BrowserPhoneContext = createContext<BrowserPhone | null>(null);
const CallSessionContext = createContext<CallSessionApi | null>(null);

/** Il telefono del browser dell'applicazione: quello di Twilio in `main.tsx`, quello finto nei test. */
export function BrowserPhoneProvider({ phone, children }: { phone: BrowserPhone; children: ReactNode }) {
  return <BrowserPhoneContext.Provider value={phone}>{children}</BrowserPhoneContext.Provider>;
}

/**
 * Il telefono del browser si scarica in sottofondo appena il guscio di chi chiama è pronto (prova del Power Dialer del 6 ottobre
 * 2026): una pagina aperta prima di un aggiornamento ha già il SDK e la sua prima chiamata parte. Una volta per pagina: il
 * caricamento è uno solo (`twilio-phone.ts`), quindi una richiesta ripetuta non scarica niente di nuovo. Si aspetta che il
 * browser sia libero (`requestIdleCallback`, al più cinque secondi) o, dove non c'è, un tempo breve: il primo disegno e le
 * prime letture non devono rallentare.
 */
export function usePreloadBrowserPhone(enabled: boolean): void {
  const phone = useContext(BrowserPhoneContext);
  useEffect(() => {
    if (!enabled || !phone) return;
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(() => phone.preload(), { timeout: 5_000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(() => phone.preload(), PRELOAD_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [enabled, phone]);
}

/** La chiamata della scheda del browser; soltanto dentro `CallSessionProvider` (il guscio). */
export function useCallSession(): CallSessionApi {
  const api = useContext(CallSessionContext);
  if (!api) throw new Error("useCallSession outside CallSessionProvider");
  return api;
}

function sameInput(left: OutcomeInput, right: OutcomeInput): boolean {
  return left.outcome === right.outcome && left.answeredOutcome === right.answeredOutcome && left.detail === right.detail && left.note === right.note;
}

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const phone = useContext(BrowserPhoneContext);
  const queryClient = useQueryClient();
  const [session, setSession] = useState<CallSession>(IDLE);
  const [pendingSteps, setPendingStepsState] = useState<readonly string[]>([]);
  const [doneSteps, setDoneSteps] = useState<readonly string[]>([]);
  // La bozza della postazione, con la chiamata a cui appartiene: non serve rileggere nulla quando cambia.
  const draft = useRef<{ callId: string; value: OutcomeDraft } | null>(null);
  // Le copie di adesso, per le funzioni che continuano dopo un `await`.
  const current = useRef<CallSession>(IDLE);
  const pending = useRef<readonly string[]>([]);
  const attempt = useRef<Attempt | null>(null);
  const tokens = useRef(new Map<TabKey, { token: string; expiresAt: number }>());
  const mounted = useRef(true);
  const inflightOutcome = useRef<Promise<OutcomeSaved> | null>(null);
  // Le chiamate di cui questa scheda ha già salvato l'esito: una lettura vecchia di «Manca l'esito…» non le riprende.
  const settled = useRef(new Set<string>());
  // L'ultima chiamata che sta ancora nascendo o abbandonandosi sul server (`dial`), finché non ha finito.
  const dialing = useRef<Promise<void> | null>(null);

  const set = useCallback((next: CallSession) => {
    current.current = next;
    setSession(next);
  }, []);

  const resetSteps = useCallback(() => {
    draft.current = null;
    pending.current = [];
    setPendingStepsState([]);
    setDoneSteps([]);
  }, []);

  // Il guscio che se ne va (uscita dall'account) chiude la chiamata e ferma i nuovi tentativi dell'esito.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      attempt.current?.browserCall?.hangUp();
      attempt.current = null;
    };
  }, []);

  // Una scelta nuova del microfono o dell'ascolto passa alla chiamata in corso.
  useEffect(
    () =>
      subscribeAudioChoice(() => {
        if (isCallLive(current.current)) attempt.current?.browserCall?.switchAudio(currentAudioChoice());
      }),
    [],
  );

  const live = isCallLive(session) || session.phase === "ending";
  useEffect(() => {
    if (!live) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // I browser di prima chiedono anche questo.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [live]);

  const toOutcome = useCallback(
    (from: OutcomeOrigin, call: CallView, notice: BrowserPhoneError | null) => {
      // La conversazione vista dal browser, dalla risposta del Lead fino a ora (la postazione la usa finché il server non dà la sua).
      const now = attempt.current;
      const answeredAt = now?.answeredAt ?? null;
      const talkSeconds = answeredAt === null ? null : Math.max(0, Math.round((Date.now() - answeredAt) / 1000));
      // Senza risposta e non chiusa da chi chiama: l'esito può darlo Gioia365 (TEL-32).
      const unanswered = now !== null && answeredAt === null && !now.hungUp;
      set({ phase: "ending", target: from.target, leadName: from.leadName, call });
      const mine = () => mounted.current && current.current.phase === "ending" && current.current.call.id === call.id;
      const settle = async () => {
        const deadline = performance.now() + 15_000;
        while (mine() && performance.now() < deadline) {
          try {
            const final = await requestCallView(from.target.tab, call.id, AbortSignal.timeout(Math.min(2_000, Math.max(1, Math.ceil(deadline - performance.now())))));
            if (!mine()) return;
            if (final.liveState === "ended") {
              const quality = final.quality?.closureQuality;
              if (quality || (!final.needsOutcome && final.outcome === null && numberDiagnosisOf(final) === null)) {
                set({ phase: "error", target: from.target, leadName: from.leadName, call: final, error: { kind: quality ?? "not_connected" } });
              } else {
                set({ phase: "outcome", target: from.target, leadName: from.leadName, call: final, missing: false, save: { state: "none" }, notice, talkSeconds, unanswered });
              }
              refreshAfterCall(queryClient, from.target.tab, from.target.leadId);
              return;
            }
          } catch (error) {
            if (mine() && error instanceof ApiRequestError && error.status === 404 && error.code === "call_not_found") {
              set({ phase: "error", target: from.target, leadName: from.leadName, call: null, error: { kind: "call_gone" } });
              return;
            }
            // Nessun esito o richiamo automatico finché la fine non è confermata dal server.
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        if (mine()) set({ phase: "error", target: from.target, leadName: from.leadName, call, error: { kind: "closure_unconfirmed" } });
      };
      void settle();
    },
    [queryClient, set],
  );

  const dialContext = useMemo((): DialContext | null => {
    if (!phone) return null;
    return {
      phone,
      read: () => current.current,
      set,
      attempt,
      isMounted: () => mounted.current,
      async token(target) {
        const key = tabKey(target.tab);
        const cached = tokens.current.get(key);
        if (cached && cached.expiresAt - Date.now() >= TOKEN_MARGIN_MS) return cached.token;
        const issued = await requestTelephonyToken(target.tab);
        tokens.current.set(key, { token: issued.token, expiresAt: Date.parse(issued.expiresAt) });
        return issued.token;
      },
      forgetToken: (target) => tokens.current.delete(tabKey(target.tab)),
      async abandon(target, call) {
        try {
          await requestCallAbandon(target.tab, call.id);
          return true;
        } catch (error) {
          // Il fornitore la conosce già: la chiudono i suoi stati. Un guasto: la chiude il giro di riconciliazione (Task 11).
          return !(error instanceof ApiRequestError && error.code === "invalid_state");
        } finally {
          refreshAfterCall(queryClient, target.tab, target.leadId);
        }
      },
      toOutcome,
      refresh: (target) => refreshAfterCall(queryClient, target.tab, target.leadId),
      audio: currentAudioChoice,
      report: (target, report) => reportPhoneError(target.tab, report),
    };
  }, [phone, queryClient, set, toOutcome]);

  const start = useCallback(
    async (input: StartCallInput) => {
      const before = current.current;
      if (before.phase !== "idle" && before.phase !== "error") return;
      const target: CallTarget = { tab: input.tab, leadId: input.leadId, phone: input.phone, dialerSessionId: input.dialerSessionId ?? null };
      const { leadName } = input;
      // Senza un telefono del browser la chiamata non nasce nemmeno sul server.
      if (!dialContext) {
        set({ phase: "error", target, leadName, call: null, error: { kind: "load" } });
        return;
      }
      // Una chiamata chiusa prima che il telefono componesse si abbandona sul server quando la sua risposta arriva: una
      // pressione veloce di «Chiama» la aspetta, altrimenti il server troverebbe il Lead ancora in chiamata (`lead_in_call`).
      const run = dial(dialContext, target, leadName, dialing.current).finally(() => {
        if (dialing.current === run) dialing.current = null;
      });
      dialing.current = run;
      await run;
    },
    [dialContext, set],
  );

  const hangUp = useCallback(() => {
    if (dialContext) void hangUpCall(dialContext);
  }, [dialContext]);

  const saveOutcome = useCallback(
    (input: OutcomeInput): Promise<OutcomeSaved> => {
      const first = current.current;
      if (first.phase !== "outcome") return Promise.reject(new Error("No call waiting for its outcome"));
      if (first.save.state === "saved") return Promise.resolve({ late: first.save.late, call: first.save.call });
      // Un salvataggio già in corso (anche fra un tentativo e l'altro) è lo stesso: si aspetta quello.
      if (first.save.state === "saving" && inflightOutcome.current) return inflightOutcome.current;
      // La stessa scelta di un tentativo rifiutato si rimanda con la sua chiave; una scelta nuova ne ha una nuova.
      const requestId = first.save.state === "failed" && sameInput(first.save.input, input) ? first.save.requestId : newRequestId();
      const { target, call } = first;
      const update = (save: OutcomeSave) => {
        const now = current.current;
        if (now.phase === "outcome" && now.call.id === call.id) set({ ...now, save });
      };
      // La chiamata non manca più dell'esito (salvato, o mai arrivata al Lead): la scheda non la ripropone mentre si rilegge.
      const settle = () => {
        settled.current.add(call.id);
        queryClient.setQueryData<CallView | null>(openCallKey(target.tab, target.leadId), (open) => (open?.id === call.id ? null : open));
        refreshAfterCall(queryClient, target.tab, target.leadId);
      };
      update({ state: "saving", input, retrying: false });
      const run = async (): Promise<OutcomeSaved> => {
        let result;
        try {
          result = await sendOutcome({
            send: () => requestOutcomeSave(target.tab, call.id, { ...input, requestId }),
            retrying: () => update({ state: "saving", input, retrying: true }),
            wanted: () => {
              const now = current.current;
              return mounted.current && now.phase === "outcome" && now.call.id === call.id;
            },
          });
        } catch (error) {
          const now = current.current;
          const ended = error instanceof ApiRequestError && (error.code === "invalid_state" || error.code === "call_not_found") ? error.code : null;
          if (ended !== null && now.phase === "outcome" && now.call.id === call.id) {
            // La chiamata è finita senza arrivare al Lead (il server l'ha chiusa non valida), oppure il server non la trova più
            // (il Lead eliminato per sempre nel frattempo): non ha esito, e la postazione lo dice invece di chiederlo ancora, così
            // «Chiama» torna e la barra si chiude (minore di R2 per R3).
            attempt.current = null;
            resetSteps();
            set({ phase: "error", target, leadName: now.leadName, call, error: { kind: ended === "invalid_state" ? "not_connected" : "call_gone" } });
            settle();
            throw error;
          }
          update({ state: "failed", input, error, requestId });
          throw error;
        }
        // Resta l'esito che la chiamata aveva già (`late`): i passi dopo l'esito sono i suoi.
        const saved =
          result.kind === "saved"
            ? result.call
            : result.existing === null
              ? call
              : { ...call, outcome: result.existing.outcome, answeredOutcome: result.existing.answeredOutcome };
        update({ state: "saved", call: saved, late: result.kind === "late" });
        settle();
        return { late: result.kind === "late", call: saved };
      };
      const promise = run().finally(() => {
        if (inflightOutcome.current === promise) inflightOutcome.current = null;
      });
      inflightOutcome.current = promise;
      return promise;
    },
    [queryClient, resetSteps, set],
  );

  const close = useCallback(() => {
    const now = current.current;
    // Soltanto la diagnosi di una chiamata che non ha contato si lascia senza esito («Controllo dopo»): mai una chiamata valida (TEL-18).
    const optionalNumberProblem = now.phase === "outcome" && numberDiagnosisOf(now.call) !== null && now.save.state !== "saving" && now.save.state !== "saved";
    if (now.phase !== "outcome" || (!optionalNumberProblem && (now.save.state !== "saved" || pending.current.length > 0))) return false;
    attempt.current = null;
    resetSteps();
    set(IDLE);
    return true;
  }, [resetSteps, set]);

  const acceptAutomaticOutcome = useCallback(
    (callId: string): boolean => {
      const now = current.current;
      if (now.phase !== "outcome" || now.call.id !== callId || now.save.state !== "none") return false;
      // Come dopo un salvataggio: la chiamata non manca più dell'esito, la scheda non la ripropone mentre si rilegge.
      settled.current.add(callId);
      queryClient.setQueryData<CallView | null>(openCallKey(now.target.tab, now.target.leadId), (open) => (open?.id === callId ? null : open));
      refreshAfterCall(queryClient, now.target.tab, now.target.leadId);
      attempt.current = null;
      resetSteps();
      set(IDLE);
      return true;
    },
    [queryClient, resetSteps, set],
  );

  const resume = useCallback(
    (input: { tab: TabContext; call: CallView; leadName: string }) => {
      const now = current.current;
      if (now.phase !== "idle" || input.call.leadId === null || settled.current.has(input.call.id)) return;
      set({
        phase: "outcome",
        target: { tab: input.tab, leadId: input.call.leadId, phone: null, dialerSessionId: null },
        leadName: input.leadName,
        call: input.call,
        missing: true,
        save: { state: "none" },
        notice: null,
        // La conversazione il browser non l'ha vista: la sua durata, se c'è, è quella del server (`call.durationSeconds`).
        talkSeconds: null,
        unanswered: false,
      });
    },
    [set],
  );

  const setPendingSteps = useCallback((steps: readonly string[]) => {
    const before = pending.current;
    if (before.length === steps.length && before.every((step, index) => step === steps[index])) return;
    pending.current = [...steps];
    setPendingStepsState(pending.current);
  }, []);

  const markStepDone = useCallback((step: string) => {
    if (current.current.phase !== "outcome") return;
    setDoneSteps((before) => (before.includes(step) ? before : [...before, step]));
  }, []);

  const draftOf = useCallback((callId: string) => (draft.current?.callId === callId ? draft.current.value : null), []);

  const keepDraft = useCallback((callId: string, value: OutcomeDraft) => {
    const now = current.current;
    if (now.phase === "outcome" && now.call.id === callId) draft.current = { callId, value };
  }, []);

  const dismiss = useCallback(() => {
    if (current.current.phase === "error") set(IDLE);
  }, [set]);

  const stepsDone = session.phase === "outcome" && session.save.state === "saved" && pendingSteps.length === 0;
  const api = useMemo<CallSessionApi>(
    () => ({ session, stepsDone, start, hangUp, saveOutcome, close, acceptAutomaticOutcome, resume, setPendingSteps, pendingSteps, doneSteps, markStepDone, draftOf, keepDraft, dismiss }),
    [session, stepsDone, start, hangUp, saveOutcome, close, acceptAutomaticOutcome, resume, setPendingSteps, pendingSteps, doneSteps, markStepDone, draftOf, keepDraft, dismiss],
  );
  return <CallSessionContext.Provider value={api}>{children}</CallSessionContext.Provider>;
}
